import React, { useState, useMemo, useEffect, useRef } from 'react'
import { List } from 'immutable'
import PropTypes from 'prop-types'
import useMultiRef from '@aytm/lib/hooks/useMultiRef'
import useMountedRef from '@aytm/lib/hooks/useMountedRef'

import ClickTarget from '@aytm/lib/rfd-ui/components/basic/click-target.jsx'
import RollDown from '@aytm/lib/rfd-ui/components/basic/rolldown'

import style from '@aytm/lib/rfd-ui/styles/basic/dropdown-menu.module.scss'

/* DropdownMenu */

DropdownMenu.propTypes = {
  header: PropTypes.func,
  footer: PropTypes.func,
  items: PropTypes.oneOfType([PropTypes.instanceOf(List), PropTypes.func]).isRequired,
  className: PropTypes.string,
  openedPanelID: PropTypes.string,
  defaultPanel: PropTypes.func,
  containerExpander: PropTypes.object
}
function DropdownMenuPropsEqual(prevProps, nextProps) {
  return (
    prevProps.items === nextProps.items &&
    prevProps.header === nextProps.header &&
    prevProps.className === nextProps.className &&
    prevProps.openedPanelID === nextProps.openedPanelID
  )
}
function DropdownMenu({
  header,
  footer,
  items: itemsSource,
  className = '',
  openedPanelID,
  defaultPanel,
  containerExpander
} = {}) {
  const [openedPanelIndex, setOpenedPanelIndex] = useState()
  // ID of the item whose panel is open. The index alone goes stale for ONE render when
  // the items list reshuffles (the re-point effect below runs post-render): items.get(stale)
  // is a different/absent item, so the open panel unmounted for a single commit — enough to
  // fire its cleanup effects and cascade into closing for good. The ID lets the render
  // resolve the panel across that gap; the effect then repairs the index.
  const refOpenedItemID = useRef(null)
  const items = 'function' === typeof itemsSource ? itemsSource() : itemsSource

  const [refRootNode, refLastItems, refCurrentItems, refContainerExpander, refLastOpenedPanelIndex] = useMultiRef(
    null,
    items,
    items,
    containerExpander,
    openedPanelIndex
  )
  refCurrentItems.current = items
  refContainerExpander.current = containerExpander

  // if items changed and panel is opened - open the panel with the same id
  useEffect(() => {
    if (refLastItems.current !== items) {
      if (null != refLastItems.current && null != items && null != openedPanelIndex) {
        // find new opened panel by ID
        const lastOpenedItem = refLastItems.current.get(openedPanelIndex)
        if (lastOpenedItem && lastOpenedItem.has('id')) {
          const lastOpenedItemID = lastOpenedItem.get('id')
          const currentOpenedItemIndex = items.findIndex(item => item.get('id') === lastOpenedItemID)
          // findIndex returns -1 when the opened item is gone from the new list;
          // Immutable.List.get(-1) is NEGATIVE indexing (the LAST item), which used
          // to silently re-open the last item's panel. Guard so a vanished item
          // simply closes its panel instead.
          const currentOpenedItem = currentOpenedItemIndex >= 0 ? items.get(currentOpenedItemIndex) : undefined
          if (currentOpenedItem && currentOpenedItem.has('panel')) {
            setOpenedPanelIndex(currentOpenedItemIndex)
          } else {
            // Gone from the list, or still present but no longer carrying a panel: either way
            // there is nothing to re-point at. Clearing both is what lets a later open recover —
            // leaving them stale would keep the render-time id lookup permanently unsatisfiable.
            refOpenedItemID.current = null
            setOpenedPanelIndex(null)
          }
        }
      }
      refLastItems.current = items
    }
  }, [items, openedPanelIndex, refLastItems])

  let openedPanelItem = null == openedPanelIndex ? null : items.get(openedPanelIndex)
  // Stale-index gap (see refOpenedItemID above): when the item at the stored index is not
  // the one whose panel is open, resolve by ID so the panel survives the render between an
  // items reshuffle and the index-repair effect.
  if (
    null != openedPanelIndex &&
    null != refOpenedItemID.current &&
    (!openedPanelItem || openedPanelItem.get('id') !== refOpenedItemID.current)
  ) {
    const byId = items.find(it => it.get('id') === refOpenedItemID.current)
    // No match means the open panel's item is gone this render. Render nothing rather than the
    // unrelated item that now sits at the stale index — the repair effect closes it next commit.
    openedPanelItem = byId && byId.has('panel') ? byId : null
  }
  const openedPanel = useMemo(() => {
    if (null == openedPanelItem) {
      return defaultPanel ? defaultPanel() : null
    } else {
      const panel = openedPanelItem.get('panel')
      return 'function' === typeof panel ? panel() : null
    }
  }, [openedPanelItem, defaultPanel])

  useEffect(() => {
    const openedItemIndex =
      null != openedPanelID
        ? refCurrentItems.current.findIndex(item => item.get('id') === openedPanelID && item.has('panel'))
        : -1
    if (openedItemIndex >= 0) {
      refOpenedItemID.current = openedPanelID
      setOpenedPanelIndex(openedItemIndex)
    }
  }, [openedPanelID, refCurrentItems])

  useEffect(() => {
    if (
      null != refContainerExpander.current &&
      refLastOpenedPanelIndex.current !== openedPanelIndex &&
      null != refRootNode.current
    ) {
      refContainerExpander.current.expand(refRootNode.current)
    }
    refLastOpenedPanelIndex.current = openedPanelIndex
  }, [openedPanelIndex, refContainerExpander, refRootNode, refLastOpenedPanelIndex])

  const headerContent = 'function' === typeof header ? header(items) : null
  const footerContent = 'function' === typeof footer ? footer(items) : null

  return (
    <div className={`rfd-ui-dropdown-menu ${style.component} ${className}`} role="section" ref={refRootNode}>
      {openedPanel && <div className={style.panel}>{openedPanel}</div>}
      <div className={style.menu}>
        {null != headerContent && (
          <>
            <div className={`rfd-ui-dropdown-menu-header ${style.menuHeader}`} role="sectionhead">
              {headerContent}
            </div>
            <div className={style.hr} role="separator" />
          </>
        )}
        {items && (
          <div className={`rfd-ui-dropdown-menu-list ${style.list}$`} role="menu" aria-orientation="vertical">
            {items.toArray().map((item, index) => {
              // A hidden item hosts a RETAINED open panel (its action no longer applies to the
              // target) without rendering a stale menu row — the panel keeps rendering via
              // openedPanelItem above, indexes stay aligned with the items List.
              if (item.get('hidden')) return null
              const icon = item.get('icon')
              const iconSize = item.get('iconSize') || 22
              const iconClassName = item.get('iconClassName') || ''
              const textSource = item.get('text')
              const text = 'function' === typeof textSource ? textSource() : textSource
              // Highlight + aria-expanded track the SAME item the panel renders from
              // (openedPanelItem, resolved by ID above), not the raw index — otherwise a
              // reshuffle points them at whatever item now sits at the stale index. For
              // id-less items openedPanelItem is items.get(openedPanelIndex), so this
              // degrades to the old index-based behavior.
              const itemOpened = null != openedPanelItem && item === openedPanelItem
              const iconStyle =
                icon && iconSize
                  ? {
                      fontSize: iconSize,
                      width: iconSize,
                      height: iconSize,
                      lineHeight: `${iconSize}px`,
                      top: (16 - iconSize) / 2,
                      left: (22 - iconSize) / 2
                    }
                  : null
              const onSelectItem = () => {
                if (item.has('panel')) {
                  const closing = index === openedPanelIndex
                  refOpenedItemID.current = closing ? null : item.get('id')
                  setOpenedPanelIndex(closing ? null : index)
                } else {
                  refOpenedItemID.current = null
                  setOpenedPanelIndex(null)
                  const onSelect = item.get('onSelect')
                  if ('function' === typeof onSelect) {
                    onSelect(item, index)
                  }
                }
              }
              return (
                <a
                  className={`rfd-ui-dropdown-menu-item ${style.item}${itemOpened ? ' ' + style.itemOpened : ''}`}
                  href={item.has('link') ? item.getIn(['link', 'href']) : void 0}
                  rel={item.has('link') ? item.getIn(['link', 'rel']) : void 0}
                  target={item.has('link') ? item.getIn(['link', 'target']) : void 0}
                  title={item.get('title')}
                  aria-label={item.get('title') || text}
                  role="menuitem"
                  tabIndex="0"
                  aria-haspopup={item.has('panel')}
                  aria-expanded={itemOpened}
                  onClick={onSelectItem}
                  onKeyDown={event => {
                    if (' ' === event.key) {
                      onSelectItem()
                      void (event.preventDefault && event.preventDefault())
                    } else if ('Enter' === event.key) {
                      onSelectItem()
                    }
                  }}
                  key={index}
                >
                  {icon && (
                    <span className={`rfd-ui-dropdown-menu-item-icon ${style.itemIcon}`} role="presentation">
                      <i className={`${icon} ${iconClassName}`} style={iconStyle} />
                    </span>
                  )}
                  <span className={`rfd-ui-dropdown-menu-item-text ${style.itemText}`}>{text}</span>
                </a>
              )
            })}
          </div>
        )}
        {null != footerContent && (
          <div className={`rfd-ui-dropdown-menu-footer ${style.menuFooter}`}>{footerContent}</div>
        )}
      </div>
    </div>
  )
}

/* DropdownMenuButton */

DropdownMenuButton.propTypes = {
  children: PropTypes.arrayOf(PropTypes.func.isRequired).isRequired,
  className: PropTypes.string,
  rollDown: PropTypes.number,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  openMenuRef: PropTypes.shape({ current: PropTypes.any }),
  defaultOpenedPanelID: PropTypes.string,
  containerExpander: PropTypes.object,
  onOpenChange: PropTypes.func
}
function DropdownMenuButton({
  children,
  className,
  rollDown = 0,
  onOpen,
  onClose,
  openMenuRef,
  defaultOpenedPanelID,
  containerExpander,
  onOpenChange
} = {}) {
  const [openedPanelID, setOpenedPanelID] = useState(defaultOpenedPanelID)
  const mountedRef = useMountedRef()

  useEffect(() => {
    if (null != openedPanelID) {
      containerExpander?.adjustContainer?.()
    }
  }, [openedPanelID, containerExpander])

  // The `openMenuRef.current(panelID)` morph is a ONE-SHOT command, not persistent state: reset it
  // to null once the inner menu has consumed it (the inner runs its open effect before this parent
  // effect, so the panel is already open and persists via the inner menu's own index). Without this
  // reset, navigating elsewhere via a menu-item click leaves `openedPanelID` stale, and re-issuing
  // the SAME panel id no-ops on the `openedPanelID !== openPanelID` guard — so a cross-panel link
  // (e.g. Archive → Manage budget access) stops working the second time within one open session.
  useEffect(() => {
    if (null != openedPanelID && mountedRef.current) setOpenedPanelID()
  }, [openedPanelID])

  return (
    <ClickTarget
      resetOnClickOutside={true}
      className={className}
      onFocus={onOpen}
      onBlur={() => void (null != openedPanelID && mountedRef.current && setOpenedPanelID(), onClose && onClose())}
      onOpenChange={onOpenChange}
    >
      {(opened, setOpened) => {
        const close = () => void (mountedRef.current && setOpened(false))
        if (openMenuRef) {
          openMenuRef.current = openPanelID => {
            if (openedPanelID !== openPanelID) {
              void (mountedRef.current && setOpenedPanelID(openPanelID))
            }
            if (openPanelID && !opened) {
              void (mountedRef.current && setOpened(true))
            }
          }
        }
        return [
          children[0] ? children[0](opened, setOpened) : null,
          children[1] ? (
            <RollDown duration={rollDown} opened={opened} containerExpander={containerExpander} key="rolldown">
              {transitionState => children[1](close, openedPanelID, transitionState)}
            </RollDown>
          ) : null
        ]
      }}
    </ClickTarget>
  )
}

/* exports */

export { DropdownMenu, DropdownMenuButton }

export default React.memo(DropdownMenu, DropdownMenuPropsEqual)
